import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// Browser detection helper
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Edge')) browser = 'Edge';
  
  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  
  return {
    browser,
    userAgent: ua,
    cookiesEnabled: navigator.cookieEnabled,
    localStorage: typeof localStorage !== 'undefined',
    thirdPartyCookies: document.cookie.indexOf('test') !== -1 || true, // Basic check
    isIOS,
  };
};

// Debug logger
const authDebug = (message, data = null) => {
  const timestamp = new Date().toISOString();
  console.log(`[Auth ${timestamp}] ${message}`, data || '');
};

// Track processed session IDs to prevent double-processing (React StrictMode issue)
// Using sessionStorage to persist across React re-renders but clear on new browser session
// Also use in-memory fallback for iOS private browsing where sessionStorage may fail
const processedSessionsMemory = new Set();

const getProcessedSessions = () => {
  try {
    const stored = sessionStorage.getItem('processed_auth_sessions');
    const fromStorage = stored ? new Set(JSON.parse(stored)) : new Set();
    // Merge with memory fallback
    processedSessionsMemory.forEach(s => fromStorage.add(s));
    return fromStorage;
  } catch {
    // sessionStorage failed (iOS private browsing) - use memory only
    return new Set(processedSessionsMemory);
  }
};

const markSessionProcessed = (sessionId) => {
  // Always add to memory fallback
  processedSessionsMemory.add(sessionId);
  
  try {
    const sessions = getProcessedSessions();
    sessions.add(sessionId);
    // Keep only last 10 sessions to avoid storage bloat
    const arr = Array.from(sessions).slice(-10);
    sessionStorage.setItem('processed_auth_sessions', JSON.stringify(arr));
  } catch {
    // Ignore storage errors - memory fallback will handle it
  }
};

const isSessionProcessed = (sessionId) => {
  // Check both memory and storage
  if (processedSessionsMemory.has(sessionId)) return true;
  return getProcessedSessions().has(sessionId);
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [pending2FA, setPending2FA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const processingRef = useRef(false);

  // Global error handler for postMessage errors (mobile Google OAuth)
  useEffect(() => {
    const handleError = (event) => {
      if (event.message && event.message.includes('postMessage')) {
        console.log('[Auth] Suppressed postMessage error (mobile OAuth):', event.message);
        event.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  // Show toast when error changes
  useEffect(() => {
    if (error) {
      toast.error(error, { duration: 5000 });
      // Clear error after showing
      setTimeout(() => setError(null), 100);
    }
  }, [error]);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      authDebug('Checking existing session...');
      try {
        // Re-read token from localStorage in case api service was initialized before it was set
        const token = localStorage.getItem('gym_notebook_auth_token');
        if (token) {
          // Ensure api service has the latest token
          api.setToken(token);
          authDebug('Found existing token, validating...');
          try {
            const userData = await api.getMe();
            authDebug('Session valid, user:', userData?.email);
            
            // If 2FA is enabled and device is NOT trusted, force re-verification
            if (userData.two_factor_enabled && !userData.device_trusted) {
              authDebug('2FA enabled but device not trusted — requiring verification');
              try {
                // Initiate 2FA challenge
                const challengeResp = await api.post('/security/2fa/challenge', {});
                if (challengeResp.pending_token) {
                  setPending2FA({
                    pendingToken: challengeResp.pending_token,
                    emailHint: userData.email,
                    suspicious: false,
                    suspiciousReasons: [],
                  });
                  // Don't set user yet — wait for 2FA verification
                  setLoading(false);
                  return;
                }
              } catch (challengeErr) {
                authDebug('2FA challenge failed, allowing login:', challengeErr.message);
              }
            }
            
            setUser(userData);
          } catch (err) {
            authDebug('Session invalid, clearing token', err.message);
            localStorage.removeItem('gym_notebook_auth_token');
            api.setToken(null);
          }
        } else {
          authDebug('No existing token found');
        }
      } catch (e) {
        authDebug('Auth check error:', e.message);
      }
      setLoading(false);
    };

    try {
      // Check for Google OAuth callback - session_id can come in URL hash OR query params
      const hash = window.location.hash;
      const search = window.location.search;
      let sessionId = null;
      let authError = null;
      let errorDescription = null;
      
      console.log('[Auth] === OAuth Callback Check ===');
      console.log('[Auth] Full URL:', window.location.href);
      console.log('[Auth] Origin:', window.location.origin);
      console.log('[Auth] Hash:', hash);
      console.log('[Auth] Search:', search);
      
      authDebug('Checking URL for OAuth callback...', { hash: hash?.substring(0, 50), search: search?.substring(0, 50) });
      
      // First check hash (fragment)
      if (hash) {
        const hashParams = new URLSearchParams(hash.substring(1));
        sessionId = hashParams.get('session_id');
        authError = hashParams.get('error');
        errorDescription = hashParams.get('error_description');
        console.log('[Auth] From hash - sessionId:', sessionId ? sessionId.substring(0, 10) + '...' : null);
        console.log('[Auth] From hash - error:', authError);
      }
      
      // Then check query params if not found in hash
      if (!sessionId && !authError && search) {
        const queryParams = new URLSearchParams(search);
        sessionId = queryParams.get('session_id');
        authError = queryParams.get('error');
        errorDescription = queryParams.get('error_description');
        console.log('[Auth] From query - sessionId:', sessionId ? sessionId.substring(0, 10) + '...' : null);
        console.log('[Auth] From query - error:', authError);
      }
      
      // IMPORTANT: Skip if this is a Stripe checkout session (starts with cs_)
      // Stripe sessions should be handled by the payment component, not auth
      if (sessionId && sessionId.startsWith('cs_')) {
        console.log('[Auth] Detected Stripe checkout session, skipping Google auth processing');
        checkSession();
        return;
      }
      
      // Handle errors from OAuth flow
      if (authError) {
        const browserInfo = getBrowserInfo();
        console.log('[Auth] OAuth error detected:', authError, errorDescription);
        authDebug('OAuth error detected', { authError, errorDescription, browserInfo });
        
        let displayError = errorDescription || authError || 'Authentication failed';
        
        // Provide browser-specific guidance
        if (browserInfo.browser === 'Firefox' && (authError === 'invalid_request' || displayError.includes('state'))) {
          displayError = 'Firefox may be blocking sign-in cookies. Try: Settings → Privacy → disable Enhanced Tracking Protection for this site, or use Chrome/Safari.';
        } else if (displayError.includes('state') || displayError.includes('expired')) {
          displayError = 'Sign-in session expired. Please try again. If this persists, try a different browser.';
        }
        
        setError(displayError);
        window.history.replaceState({}, document.title, window.location.pathname);
        setLoading(false);
        return;
      }
      
      if (sessionId) {
        authDebug('Found session_id, processing Google auth...', sessionId.substring(0, 10) + '...');
        processGoogleAuth(sessionId);
      } else {
        checkSession();
      }
    } catch (e) {
      authDebug('Auth initialization error:', e.message);
      setError('Authentication initialization failed. Please refresh the page.');
      setLoading(false);
    }
  }, []);

  const processGoogleAuth = async (sessionId, retryCount = 0) => {
    // Prevent double-processing (React StrictMode can call useEffect twice)
    if (isSessionProcessed(sessionId) || processingRef.current) {
      console.log('[Auth] Session already processed or processing, skipping...', sessionId.substring(0, 10));
      authDebug('Session already processed or processing, skipping...', { sessionId: sessionId.substring(0, 10) });
      setLoading(false);
      return;
    }
    
    // Mark as processing immediately
    processingRef.current = true;
    markSessionProcessed(sessionId);
    
    console.log('[Auth] Processing session:', sessionId.substring(0, 10) + '...');
    const browserInfo = getBrowserInfo();
    authDebug('Processing Google auth...', { sessionIdPrefix: sessionId.substring(0, 10), browserInfo, retryCount });
    
    try {
      // Use the api service to process the Google session
      const data = await api.processGoogleSession(sessionId);
      
      // Check if 2FA is required for Google login
      if (data.requires_2fa) {
        authDebug('2FA required for Google login');
        console.log('[Auth] Setting pending2FA state:', JSON.stringify({
          pendingToken: data.pending_token?.substring(0, 10) + '...',
          emailHint: data.email_hint,
          suspicious: !!data.suspicious,
        }));
        setPending2FA({
          pendingToken: data.pending_token,
          emailHint: data.email_hint || '',
          suspicious: !!data.suspicious,
          suspiciousReasons: data.suspicious_reasons || [],
        });
        setLoading(false);
        processingRef.current = false;
        // Clean URL so refresh doesn't retry
        try {
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch { /* ignore */ }
        return;
      }
      
      console.log('[Auth] Session processed successfully, user:', data.user?.email);
      authDebug('Google auth successful', { userId: data.user?.user_id, hasToken: !!data.token });
      
      if (data.token) {
        api.setToken(data.token);
        authDebug('Token saved to localStorage');
      }
      setUser(data.user);
      toast.success(`Welcome, ${data.user?.name || 'User'}!`);
      
      // Clean up URL - remove the hash (wrapped in try-catch for mobile compatibility)
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (historyErr) {
        console.log('[Auth] Could not clean URL:', historyErr.message);
        // On iOS, don't force reload - just leave the URL as is
        // The app will still work, just with a messy URL
      }
    } catch (err) {
      authDebug('Google auth failed', { error: err.message, browserInfo, retryCount });
      
      // Retry up to 2 times for transient errors (520, 502, network errors)
      const errorMsg = err.message || '';
      const isTransientError = errorMsg.includes('520') || 
                               errorMsg.includes('502') || 
                               errorMsg.includes('503') ||
                               errorMsg.includes('network') ||
                               errorMsg.includes('fetch') ||
                               errorMsg.includes('timeout');
      
      if (isTransientError && retryCount < 2) {
        console.log('[Auth] Transient error, retrying...');
        authDebug(`Retrying Google auth (attempt ${retryCount + 2})...`);
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        processingRef.current = false; // Allow retry
        // Remove from sessionStorage to allow retry
        try {
          const sessions = getProcessedSessions();
          sessions.delete(sessionId);
          sessionStorage.setItem('processed_auth_sessions', JSON.stringify(Array.from(sessions)));
        } catch { /* ignore */ }
        return processGoogleAuth(sessionId, retryCount + 1);
      }
      
      // Clean up URL even on error (wrapped in try-catch for mobile compatibility)
      try {
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (historyErr) {
        console.log('[Auth] Could not clean URL on error:', historyErr.message);
      }
      
      console.log('[Auth] Auth failed with error:', errorMsg);
      
      // Check for specific error messages and provide helpful guidance
      let displayError = '';
      
      if (errorMsg.includes('Invalid state') || errorMsg.includes('invalid_request') || errorMsg.includes('state')) {
        if (browserInfo.browser === 'Firefox') {
          displayError = 'Firefox privacy settings may be blocking sign-in. Try: 1) Click the shield icon in the address bar and disable tracking protection, or 2) Use Chrome/Safari instead.';
        } else {
          displayError = 'Sign-in session expired or invalid. This can happen if you waited too long or refreshed the page. Please try again.';
        }
      } else if (errorMsg.includes('expired') || errorMsg.includes('not found')) {
        displayError = 'Sign-in session expired. Please click "Sign in with Google" again.';
      } else if (errorMsg.includes('520') || errorMsg.includes('502') || errorMsg.includes('503')) {
        displayError = 'Server temporarily unavailable. Please try again in a moment.';
      } else if (errorMsg.includes('network') || errorMsg.includes('fetch') || errorMsg.includes('Failed to fetch')) {
        displayError = 'Connection error during sign-in. Please try again. If this persists, try clearing your browser cache or using a different browser.';
      } else {
        displayError = `Google sign-in failed: ${errorMsg || 'Unknown error'}. Please try again or use email/password.`;
      }
      
      setError(displayError);
    } finally {
      processingRef.current = false;
      setLoading(false);
    }
  };

  const register = useCallback(async (name, email, password) => {
    authDebug('Registering user...', { email });
    setError(null);
    try {
      const data = await api.register(name, email, password);
      authDebug('Registration successful', { userId: data.user?.user_id });
      if (data.token) {
        api.setToken(data.token);
        authDebug('Token saved after registration');
      }
      setUser(data.user);
      return data;
    } catch (err) {
      authDebug('Registration failed', { error: err.message });
      
      // Provide user-friendly error messages
      let displayError = err.message || 'Registration failed';
      if (displayError.includes('already exists') || displayError.includes('duplicate') || displayError.includes('already registered')) {
        displayError = 'An account with this email already exists. Please sign in instead.';
      } else if (displayError.includes('invalid email')) {
        displayError = 'Please enter a valid email address.';
      } else if (displayError.includes('password')) {
        displayError = 'Password must be at least 6 characters long.';
      } else if (displayError.includes('network') || displayError.includes('fetch')) {
        displayError = 'Network error. Please check your connection and try again.';
      } else if (displayError.includes('Server error') || displayError.includes('500')) {
        displayError = 'Server error. Please try again in a moment.';
      }
      
      setError(displayError);
      throw new Error(displayError);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    authDebug('Logging in user...', { email });
    setError(null);
    try {
      const data = await api.login(email, password);
      
      // If 2FA is required, pass it up to the caller
      if (data.requires_2fa) {
        return data;
      }
      
      authDebug('Login successful', { userId: data.user?.user_id });
      if (data.token) {
        api.setToken(data.token);
        authDebug('Token saved after login');
      }
      setUser(data.user);
      return data;
    } catch (err) {
      authDebug('Login failed', { error: err.message });
      
      // Provide user-friendly error messages
      let displayError = err.message || 'Login failed';
      if (displayError.includes('Invalid') || displayError.includes('incorrect') || displayError.includes('credentials')) {
        displayError = 'Invalid email or password. Please check your credentials and try again.';
      } else if (displayError.includes('not found') || displayError.includes('no user')) {
        displayError = 'No account found with this email. Please sign up first.';
      } else if (displayError.includes('network') || displayError.includes('fetch')) {
        displayError = 'Network error. Please check your connection and try again.';
      } else if (displayError.includes('blocked')) {
        displayError = 'This device has been blocked. Contact support.';
      }
      
      setError(displayError);
      throw new Error(displayError);
    }
  }, []);

  // Complete 2FA login after code verification
  const complete2FALogin = useCallback((data) => {
    if (data.token) {
      api.setToken(data.token);
    }
    setUser(data.user);
  }, []);

  const loginWithGoogle = useCallback(() => {
    const browserInfo = getBrowserInfo();
    authDebug('Initiating Google login...', browserInfo);
    
    // Show warning for Firefox users
    if (browserInfo.browser === 'Firefox') {
      toast.info('Tip: If sign-in fails, try disabling Enhanced Tracking Protection or use Chrome/Safari.', { duration: 5000 });
    }
    
    // Redirect to Google OAuth via Emergent Auth
    const redirectUrl = window.location.origin;
    console.log('[Auth] Google login initiated');
    console.log('[Auth] Current origin:', window.location.origin);
    console.log('[Auth] Redirect URL:', redirectUrl);
    console.log('[Auth] Full auth URL:', `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`);
    authDebug('Redirecting to auth...', { redirectUrl });
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    authDebug('Logging out...');
    try {
      await api.logout();
      authDebug('Logout successful');
    } catch (err) {
      authDebug('Logout error (ignored)', err.message);
    } finally {
      setUser(null);
      toast.success('Logged out successfully');
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await api.getMe();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      throw err;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    complete2FALogin,
    pending2FA,
    setPending2FA,
    loginWithGoogle,
    logout,
    refreshUser,
    setError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
